import React from "react";
import { motion } from "framer-motion";
import { Play, Pause, ArrowUpRight } from "lucide-react";
import { RELEASES } from "@/data/content";
import { useAudio } from "@/context/AudioContext";
import { Reveal } from "./Reveal";

const Catalog = () => {
  const { current, isPlaying, hasStarted, playTrack } = useAudio();

  return (
    <section id="catalog" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
            <div>
              <p className="text-sm font-medium tracking-widest text-neutral-400 mb-3">
                CATALOG
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight">
                Defining releases.
              </h2>
            </div>
            <p className="max-w-sm text-neutral-500">
              A selection of records shaped in the room. Stream on your platform or preview in the dock.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {RELEASES.map((r, i) => {
            const isStream = r.action === "stream";
            const active = !isStream && current.id === r.trackId && hasStarted;
            const playingThis = active && isPlaying;
            return (
              <Reveal key={r.id} delay={i * 0.06}>
                <div
                  data-testid={`release-card-${r.id}`}
                  className="group relative rounded-3xl border border-gray-200 bg-white p-3 transition-shadow duration-500 hover:shadow-xl"
                >
                  <div className="relative overflow-hidden rounded-2xl aspect-square">
                    <img
                      src={r.cover}
                      alt={`${r.title} cover`}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="absolute top-3 left-3 rounded-full bg-white/85 backdrop-blur px-3 py-1 text-xs font-medium text-neutral-800">
                      {r.genre}
                    </span>
                    {r.tag && (
                      <span className="absolute top-3 right-3 rounded-full bg-black/70 backdrop-blur px-3 py-1 text-xs font-medium text-white">
                        {r.tag}
                      </span>
                    )}
                    {isStream ? (
                      <a
                        href={r.href}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`play-release-${r.id}`}
                        aria-label={`Stream ${r.title}`}
                        className="absolute bottom-3 right-3 flex h-12 w-12 items-center justify-center rounded-full bg-white text-black shadow-lg translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 hover:bg-playing hover:text-white"
                      >
                        <Play size={18} className="ml-0.5 fill-current" />
                      </a>
                    ) : (
                      <button
                        data-testid={`play-release-${r.id}`}
                        onClick={() => playTrack(r.trackId)}
                        aria-label={`Play ${r.title}`}
                        className={`absolute bottom-3 right-3 flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all duration-300 ${
                          playingThis
                            ? "bg-playing text-white scale-100"
                            : "bg-white text-black translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100"
                        }`}
                      >
                        {playingThis ? (
                          <Pause size={18} className="fill-current" />
                        ) : (
                          <Play size={18} className="ml-0.5 fill-current" />
                        )}
                      </button>
                    )}
                    {isStream && (
                      <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-black/70 backdrop-blur px-3 py-1 text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Spotify <ArrowUpRight size={12} />
                      </span>
                    )}
                  </div>
                  <div className="flex items-start justify-between px-2 pt-4 pb-2">
                    <div className="min-w-0">
                      <h3 className="font-heading font-semibold tracking-tight truncate flex items-center gap-2">
                        {r.title}
                        {playingThis && (
                          <span className="flex h-3 items-end gap-[2px]">
                            {[0, 1, 2].map((b) => (
                              <span
                                key={b}
                                className="afr-eq-bar w-[2px] rounded-full bg-playing"
                                style={{ animationDelay: `${b * 0.15}s` }}
                              />
                            ))}
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-neutral-500 truncate">{r.artist}</p>
                    </div>
                    <span className="text-sm text-neutral-400 shrink-0 ml-2">{r.year}</span>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Catalog;
